import React, { useState } from 'react';
import {
  withStyles,
  FormControl,
  FormLabel,
  FormGroup,
  Modal,
  Typography,
  Tab,
  Tabs,
  AppBar,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import CreateIcon from '@material-ui/icons/Create';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import PF2MButton from '~/components/PF2MButton';
import {
  Card,
  CardBody,
  CardHeader,
  CardIcon,
} from '~/components/Card';
import {
  updateLoadAllocations,
  setSelectedLoadAllocation,
  setSelectedLoadAllocationValues,
  setLoadMaterialMappingsValue,
  deallocateLoadAllocations,
  updateIsTruckAllocationLoading,
  getLoadMaterialMappings,
} from '~/store/dispatch/actions';
import useTabletConfiguration from '~/services/tabletConfiguration';
import classnames from 'classnames';
import styles from '../styles';
import { getSelectedAllocationInitialState, getUpdateLoadAllocationErrorsInitialState } from '../utils';


const UpdateLoadAllocationModalV2 = ({
  isOpen,
  close,
  classes,
  container,
}) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  const { value: dispatchRealTime } = useTabletConfiguration('dispatch_real_time');
  const shouldRefresh = dispatchRealTime === '0';

  const allocations = useSelector(state => state.dispatch.loadAllocations);
  const loadAllocation = useSelector(state => state.dispatch.selectedLoadAllocation);
  const blacklistAllocations = useSelector(state => state.dispatch.blacklistAllocations);
  const loadMaterialMappings = useSelector(state => state.dispatch.loadMaterialMappings);

  const elements = useSelector(state => state.nref.elements);
  const materials = useSelector(state => state.nref.materials);
  const equipments = useSelector(state => state.nref.equipments);
  const subElements = useSelector(state => state.nref.subElements);
  const equipmentTypes = useSelector(state => state.nref.equipmentTypes);
  const equipmentGroups = useSelector(state => state.nref.equipmentGroups);

  const [errors, setErrors] = useState(getUpdateLoadAllocationErrorsInitialState());

  const truckEquipIds = [2];
  const loadEquipIds = [1, 15];
  const allocationsEquipIds = allocations?.filter(a => a.id).map(a => a.equip_id);

  const loadTypes = equipmentTypes?.filter(r => loadEquipIds.includes(r.id));
  const loadEquips = equipments?.filter(r => (
    loadEquipIds.includes(r.id_equip) && !allocationsEquipIds.includes(r.id)
  ));
  const loadGroups = equipmentGroups?.filter(r => loadEquipIds.includes(r.id_equipament));

  const truckEquips = equipments?.filter(r => truckEquipIds.includes(r.id_equip));
  const truckGroups = equipmentGroups?.filter(r => truckEquipIds.includes(r.id_equipament));
  const emptyValues = [0, '0', '', null];
  // Only the leading material rows are mandatory (currently just the first one).
  // A new mandatory row means adding a `true` here, not a new index check.
  const MATERIAL_ROW_REQUIRED = [true, false];

  const lmmRequiredKeysMapper = {
    first_element_id: 'firstElementId',
    first_element_point_id: 'firstElementPointId',
    second_element_id: 'secondElementId',
    second_element_point_id: 'secondElementPointId',
  };
  const laRequiredKeysMapper = {
    materials: 'materials',
    origin_element_id: 'originElementId',
    origin_element_point_id: 'originElementPointId',
    equip_id: 'equipId',
  };

  const filteredLMM = loadAllocation.material_mappings?.filter(
    lmm => !emptyValues.includes(lmm.material_id),
  );
  const hasLMMBeenFilled = filteredLMM?.every(
    lmm => Object.keys(lmmRequiredKeysMapper).every(
      key => !emptyValues.includes(lmm[key]),
    ),
  );

  const hasLABeenFilled = Object.entries(loadAllocation)?.filter(([k, _]) => (
    Object.keys(laRequiredKeysMapper).includes(k) && k !== 'materials'
  )).every(([_, v]) => !emptyValues.includes(v));
  const hasLAMaterial = loadAllocation?.materials.some(material => !emptyValues.includes(material));
  const disableSaveButton = !hasLMMBeenFilled || !hasLAMaterial || !hasLABeenFilled;


  const handleValueChange = (key, value, index = 0) => {
    const newItem = { ...loadAllocation, [key]: value };
    const requiredFields = Object.keys(laRequiredKeysMapper);
    const errorKey = laRequiredKeysMapper[key];

    if (requiredFields.includes(key) && emptyValues.includes(value) && key !== 'materials') {
      setErrors({ ...errors, [errorKey]: translate('validation:RequiredField') });
    } else {
      setErrors({ ...errors, [errorKey]: '' });
    }

    if (key === 'origin_element_id') {
      const elementPointKey = laRequiredKeysMapper.origin_element_point_id;

      if (emptyValues.includes(value)) {
        newItem.origin_element_point_id = 0;
        setErrors({
          ...errors,
          [errorKey]: translate('validation:RequiredField'),
          [elementPointKey]: translate('validation:RequiredField'),
        });
      } else {
        setErrors({
          ...errors,
          [errorKey]: '',
          [elementPointKey]: translate('validation:RequiredField'),
        });
      }
    }

    if (key === 'materials') {
      const isMaterialRowRequired = MATERIAL_ROW_REQUIRED[index] ?? false;

      if (value === 0) {
        newItem.materials = loadAllocation.materials;
        newItem.material_mappings = loadAllocation.material_mappings;
        newItem.materials[index] = 0;
        newItem.material_mappings[index] = (
          { ...getSelectedAllocationInitialState().material_mappings[index] }
        );

        const materialErrors = errors.materials;
        const materialMappingsErrors = errors.materialMappings;
        const clearedRowMessage = isMaterialRowRequired ? translate('validation:RequiredField') : '';

        materialErrors[index] = { material: clearedRowMessage };
        materialMappingsErrors[index] = {
          firstElementId: clearedRowMessage,
          firstElementPointId: clearedRowMessage,
          secondElementId: clearedRowMessage,
          secondElementPointId: clearedRowMessage,
        };

        setErrors({
          ...errors,
          materials: materialErrors,
          materialMappings: materialMappingsErrors,
        });
        return;
      }

      if (!loadAllocation.materials.includes(value)) {
        newItem.materials = loadAllocation.materials;
        newItem.materials[index] = value;
      }

      const foundLAMM = loadMaterialMappings.find(lmm => (
        lmm.material_id === value && lmm.equip_id === loadAllocation.equip_id
      ));
      const materialAlreadyMapped = loadAllocation.material_mappings.find(
        lmm => (lmm.material_id === value),
      );

      if (!materialAlreadyMapped) {
        if (foundLAMM && foundLAMM.id !== null) {
          newItem.material_mappings = loadAllocation.material_mappings;
          newItem.material_mappings[index] = foundLAMM;
        } else {
          newItem.material_mappings = loadAllocation.material_mappings;
          const initialMaterialMapping = (
            getSelectedAllocationInitialState().material_mappings[index]
          );
          newItem.material_mappings[index] = {
            ...initialMaterialMapping,
            material_id: value,
            equip_id: loadAllocation.equip_id,
          };
        }
      }

      // Material is now filled (for any row): the material itself is never
      // flagged, and each destination field is flagged only if still empty.
      const resolvedMapping = loadAllocation.material_mappings[index];
      const materialErrors = errors.materials;
      const materialMappingsErrors = errors.materialMappings;

      materialErrors[index] = { material: '' };
      materialMappingsErrors[index] = {
        firstElementId: emptyValues.includes(resolvedMapping.first_element_id)
          ? translate('validation:RequiredField') : '',
        firstElementPointId: emptyValues.includes(resolvedMapping.first_element_point_id)
          ? translate('validation:RequiredField') : '',
        secondElementId: emptyValues.includes(resolvedMapping.second_element_id)
          ? translate('validation:RequiredField') : '',
        secondElementPointId: emptyValues.includes(resolvedMapping.second_element_point_id)
          ? translate('validation:RequiredField') : '',
      };

      setErrors({
        ...errors,
        materials: materialErrors,
        materialMappings: materialMappingsErrors,
      });

      if (materialAlreadyMapped) {
        return;
      }
    }

    dispatch(setSelectedLoadAllocationValues(newItem));
  };

  const renderFormControl = ({
    label,
    value,
    disabled,
    onChange,
    dropdown,
    errorMessage,
  }) => (
    <FormControl>
      <FormLabel className={classes.formLabel}>
        {label}
      </FormLabel>
      <FormGroup>
        <PF2MSearchSelectOutlined
          placeholder={null}
          value={value || 0}
          className={classnames(classes.field)}
          onChange={onChange}
          disabled={disabled}
          styles={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          {[{ value: 0, label: translate('common:Select') }].concat(...dropdown)}
        </PF2MSearchSelectOutlined>
        {errorMessage && <Typography color="error">{errorMessage}</Typography>}
      </FormGroup>
    </FormControl>
  );

  const handleClose = () => {
    dispatch(setSelectedLoadAllocation(getSelectedAllocationInitialState()));
    setErrors(getUpdateLoadAllocationErrorsInitialState());
    close();
  };

  const onChangMaterialMapClosure = ({
    event, index, materialMapKey, cascadeMapKey = null,
  }) => {
    const { target: { value } } = event;
    const key = lmmRequiredKeysMapper[materialMapKey];
    const lmmErrors = [...errors.materialMappings];

    if (emptyValues.includes(value)) {
      lmmErrors[index][key] = translate('validation:RequiredField');
      setErrors({ ...errors, materialMappings: lmmErrors });
    } else {
      lmmErrors[index][key] = '';
      setErrors({ ...errors, materialMappings: lmmErrors });
    }

    const newItem = { ...loadAllocation };
    newItem.material_mappings[index][materialMapKey] = value;

    if (cascadeMapKey !== null) {
      const cascadeKey = lmmRequiredKeysMapper[cascadeMapKey];
      newItem.material_mappings[index][cascadeMapKey] = 0;
      lmmErrors[index][cascadeKey] = translate('validation:RequiredField');
      setErrors({ ...errors, materialMappings: lmmErrors });
    }

    dispatch(setSelectedLoadAllocationValues(newItem));
  };

  const renderMaterialRow = (materialId, index) => {
    const materialMapping = loadAllocation.material_mappings[index];

    return (
      <React.Fragment key={`${materialId}-${index}`}>
        {renderFormControl({
          label: translate('common:Material'),
          value: materialId ?? 0,
          disabled: loadAllocation.equip_id === 0,
          onChange: e => handleValueChange('materials', e.target.value, index),
          dropdown: materials.map(material => ({ value: material.id, label: material.name })),
          errorMessage: (
            errors.materials[index]?.material?.length > 0
            && errors.materials[index]?.material
          ),
        })}

        {renderFormControl({
          label: translate('common:DestinationElement'),
          value: materialMapping?.first_element_id ?? 0,
          disabled: loadAllocation.equip_id === 0 || materialId === 0,
          onChange: (event) => {
            onChangMaterialMapClosure({
              event,
              index,
              materialMapKey: 'first_element_id',
              cascadeMapKey: 'first_element_point_id',
            });
          },
          dropdown: elements.map(element => ({ value: element.id, label: element.name })),
          errorMessage: (
            errors.materialMappings[index]?.firstElementId?.length > 0
            && errors.materialMappings[index]?.firstElementId
          ),
        })}

        {renderFormControl({
          label: translate('common:DestinationSubElement'),
          value: materialMapping?.first_element_point_id ?? 0,
          disabled: loadAllocation.equip_id === 0 || materialId === 0,
          onChange: event => onChangMaterialMapClosure({
            event,
            index,
            materialMapKey: 'first_element_point_id',
          }),
          dropdown: subElements?.filter(subElement => (
            !materialMapping
            || !materialMapping.first_element_id
            || subElement.id_element === materialMapping.first_element_id
          ))
            .map(subElement => ({ value: subElement.id, label: subElement.name })),
          errorMessage: (
            errors.materialMappings[index]?.firstElementPointId?.length > 0
            && errors.materialMappings[index]?.firstElementPointId
          ),
        })}

        {renderFormControl({
          label: `${translate('common:DestinationElement')} 2`,
          value: materialMapping?.second_element_id ?? 0,
          disabled: loadAllocation.equip_id === 0 || materialId === 0,
          onChange: event => onChangMaterialMapClosure({
            event,
            index,
            materialMapKey: 'second_element_id',
            cascadeMapKey: 'second_element_point_id',
          }),
          dropdown: elements.map(e => ({ value: e.id, label: e.name })),
          errorMessage: (
            errors.materialMappings[index]?.secondElementId?.length > 0
            && errors.materialMappings[index]?.secondElementId
          ),
        })}

        {renderFormControl({
          label: `${translate('common:DestinationSubElement')} 2`,
          value: materialMapping?.second_element_point_id ?? 0,
          disabled: loadAllocation.equip_id === 0 || materialId === 0,
          onChange: event => onChangMaterialMapClosure({
            event,
            index,
            materialMapKey: 'second_element_point_id',
          }),
          dropdown: subElements
            ?.filter(r => (
              !materialMapping
              || !materialMapping.second_element_id
              || r.id_element === materialMapping.second_element_id
            ))
            .map(e => ({ value: e.id, label: e.name })),
          errorMessage: (
            errors.materialMappings[index]?.secondElementPointId?.length > 0
            && errors.materialMappings[index]?.secondElementPointId
          ),
        })}
      </React.Fragment>
    );
  };

  const defaultOption = [{ id: 0, name: translate('common:Select') }];

  const elementsDropDown = defaultOption
    .concat(...elements)
    .map(e => ({ value: e.id, label: e.name }));

  const subElementsDropDown = defaultOption
    .concat(...subElements)
    ?.filter(s => (
      loadAllocation.origin_element_id < 1
      || loadAllocation.origin_element_id === s.id_element
    )).map(s => ({ value: s.id, label: s.name }));

  const truckEquipmentsDropDown = (selectedLoadAllocation) => {
    if (!selectedLoadAllocation) {
      return [];
    }
    // if (!selectedLoadAllocation || !blacklistAllocations) {
    //   return [];
    // }

    // const blacklistAllocationByEquipment = blacklistAllocations.find(
    //   ba => ba.load_id === selectedLoadAllocation.equip_id,
    // );

    // const blacklistTrucks = blacklistAllocationByEquipment?.trucks || [];

    return truckEquips
      // ?.filter(te => !blacklistTrucks.includes(te.id))
      ?.filter(r => (
        selectedLoadAllocation.truck_groups.length < 1
        || selectedLoadAllocation.truck_groups.includes(r.id_group)
      ))
      .map(r => ({ value: r.id, label: r.name }));
  };

  return (
    <Modal
      open={isOpen}
      onClose={(event, reason) => reason === 'backdropClick' && handleClose()}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      container={container || document.body}
    >
      <div style={{ width: 1200 }}>
        <Card>
          <CardHeader icon>
            <>
              <CardIcon color="warning">
                <CreateIcon />
              </CardIcon>
              <CardIcon
                onClick={handleClose}
                style={{
                  float: 'right',
                  cursor: 'pointer',
                  background: 'darkgray',
                  borderRadius: 50,
                  boxShadow: '10px 10px 50px gray',
                }}
              >
                <CloseIcon />
              </CardIcon>
            </>
          </CardHeader>
          <CardBody>
            <div>
              <div style={{ marginBottom: 20 }}>
                <Typography
                  variant="h4"
                  align="center"
                  gutterBottom
                >
                  {
                    loadAllocation?.equip_name
                      ? <span>{`${translate('common:Allocation')}: ${loadAllocation.equip_name}`}</span>
                      : <span>{translate('common:NewAllocation')}</span>
                  }
                </Typography>
              </div>
              {
                !loadAllocation?.equip_name && (
                  <div style={{ marginBottom: 20 }}>
                    <FormControl>
                      <FormLabel className={classes.formLabel}>
                        {translate('common:EquipmentType')}
                      </FormLabel>
                      <FormGroup>
                        <PF2MSearchSelectOutlined
                          placeholder={null}
                          value={loadAllocation.equip_type_id}
                          onChange={e => handleValueChange('equip_type_id', e.target.value)}
                          className={classnames(classes.field)}
                        >
                          {[{ value: 0, label: translate('common:Select') }]
                            .concat(...loadTypes
                              .map(e => ({ value: e.id, label: e.name })))}
                        </PF2MSearchSelectOutlined>
                      </FormGroup>
                    </FormControl>
                    <FormControl style={{ marginLeft: 10 }}>
                      <FormLabel className={classes.formLabel}>
                        {translate('common:EquipmentGroup')}
                      </FormLabel>
                      <FormGroup>
                        <PF2MSearchSelectOutlined
                          placeholder={null}
                          value={loadAllocation.equip_group_id}
                          onChange={e => handleValueChange('equip_group_id', e.target.value)}
                          className={classnames(classes.field)}
                        >
                          {[{ value: 0, label: translate('common:Select') }]
                            .concat(...loadGroups
                              ?.filter(r => (loadAllocation.equip_type_id < 1
                                || loadAllocation.equip_type_id === r.id_equipament))
                              .map(e => ({ value: e.id, label: e.name })))}
                        </PF2MSearchSelectOutlined>
                      </FormGroup>
                    </FormControl>
                    <FormControl style={{ marginLeft: 10 }}>
                      <FormLabel className={classes.formLabel}>
                        {translate('common:Equipment')}
                      </FormLabel>
                      <FormGroup>
                        <PF2MSearchSelectOutlined
                          placeholder={null}
                          onChange={e => handleValueChange('equip_id', e.target.value)}
                          value={loadAllocation.equip_id}
                          className={classnames(classes.field)}
                        >
                          {[{ value: 0, label: translate('common:Select') }]
                            .concat(...loadEquips?.filter(r => (
                              loadAllocation.equip_group_id < 1
                              || loadAllocation.equip_group_id === r.id_group
                            )).map(e => ({ value: e.id, label: e.name })))}
                        </PF2MSearchSelectOutlined>
                        {errors?.equipId && <Typography color="error">{errors.equipId}</Typography>}
                      </FormGroup>
                    </FormControl>
                  </div>
                )
              }
              <div>
                <AppBar
                  position="static"
                  style={{
                    backgroundColor: 'white',
                    boxShadow: 'none',
                  }}
                >
                  <Tabs
                    value={0}
                    classes={{ root: classes.tabsRoot, indicator: classes.tabsIndicator }}
                  >
                    <Tab
                      label={translate('common:Allocation')}
                      classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
                    />
                  </Tabs>
                </AppBar>
                <div style={{ padding: 20 }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gridColumnGap: 10,
                    gridRowGap: 10,
                  }}
                  >
                    <FormControl>
                      <FormLabel className={classes.formLabel}>
                        {translate('common:Element')}
                      </FormLabel>
                      <FormGroup>
                        <PF2MSearchSelectOutlined
                          placeholder={null}
                          onChange={e => handleValueChange('origin_element_id', e.target.value)}
                          value={loadAllocation.origin_element_id}
                          className={classnames(classes.field)}
                          required
                        >
                          {elementsDropDown}
                        </PF2MSearchSelectOutlined>
                        {errors.originElementId && <Typography color="error">{errors.originElementId}</Typography>}
                      </FormGroup>
                    </FormControl>
                    <FormControl>
                      <FormLabel className={classes.formLabel}>
                        {translate('common:SubElement')}
                      </FormLabel>
                      <FormGroup>
                        <PF2MSearchSelectOutlined
                          placeholder={null}
                          onChange={e => handleValueChange('origin_element_point_id', e.target.value)}
                          value={loadAllocation.origin_element_point_id}
                          className={classnames(classes.field)}
                          required
                        >
                          {subElementsDropDown}
                        </PF2MSearchSelectOutlined>
                        {errors.originElementPointId && <Typography color="error">{errors.originElementPointId}</Typography>}
                      </FormGroup>
                    </FormControl>

                    <FormControl>
                      <FormLabel className={classes.formLabel}>
                        {translate('common:TruckEquipmentGroups')}
                      </FormLabel>
                      <FormGroup>
                        <PF2MSearchSelectOutlined
                          className={classnames(classes.field)}
                          value={loadAllocation.truck_groups}
                          onChange={e => handleValueChange('truck_groups', [...e.target.value.map(r => r.value)])}
                          multiple
                          hideSelectedOptions={false}
                          closeMenuOnSelect={false}
                          placeholder={translate('common:Select')}
                        >
                          {truckGroups.map(r => ({ value: r.id, label: r.name }))}
                        </PF2MSearchSelectOutlined>
                      </FormGroup>
                    </FormControl>
                    <FormControl>
                      <FormLabel className={classes.formLabel}>
                        {translate('common:TruckEquipments')}
                      </FormLabel>
                      <FormGroup>
                        <PF2MSearchSelectOutlined
                          className={classnames(classes.field)}
                          value={loadAllocation.trucks}
                          onChange={e => handleValueChange('trucks', [...e.target.value.map(r => r.value)])}
                          multiple
                          hideSelectedOptions={false}
                          closeMenuOnSelect={false}
                          placeholder={translate('common:Select')}
                        >
                          {truckEquipmentsDropDown(loadAllocation)}
                        </PF2MSearchSelectOutlined>
                      </FormGroup>
                    </FormControl>
                    <br />
                    {
                      loadAllocation?.materials?.map((materialId, index) => (
                        renderMaterialRow(materialId, index)
                      ))
                    }
                  </div>
                </div>
              </div>
              <div style={{
                display: 'flex',
                marginTop: 10,
                alignItems: 'center',
                justifyContent: 'center',
              }}
              >
                {!!loadAllocation.id && (
                  <PF2MButton
                    color="primary"
                    onClick={() => {
                      dispatch(deallocateLoadAllocations({
                        id: loadAllocation.id,
                        refresh: true,
                        skipLoading: false,
                        dispatchLoading: true,
                      }));
                      handleClose();
                    }}
                  >
                    <DeleteOutline className={classes.icon} />
                    {translate('common:DeleteAllocation')}
                  </PF2MButton>
                )}
                <PF2MButton
                  disabled={disableSaveButton}
                  variant="extended"
                  size="medium"
                  color="secondary"
                  onClick={() => {
                    const {
                      material_options,
                      material_mappings,
                      ...clearLoadAllocation
                    } = loadAllocation;
                    dispatch(updateIsTruckAllocationLoading(true));
                    dispatch(updateLoadAllocations({
                      item: clearLoadAllocation,
                      materialMappings: filteredLMM,
                      refresh: shouldRefresh,
                      skipLoading: true,
                      dispatchLoading: true,
                      finallyCb: () => dispatch(getLoadMaterialMappings()),
                    }));
                    handleClose();
                  }}
                  className={classes.saveButton}
                >
                  <SaveIcon className={classes.icon} />
                  {translate('common:Save')}
                </PF2MButton>
              </div>
            </div>
            <div />
          </CardBody>
        </Card>
      </div>
    </Modal>
  );
};

UpdateLoadAllocationModalV2.propTypes = {
  classes: PropTypes.object.isRequired,
  isOpen: PropTypes.bool,
  close: PropTypes.func,
  container: PropTypes.element,
};

UpdateLoadAllocationModalV2.defaultProps = {
  isOpen: false,
  close: () => null,
  container: null,
};

export default withStyles(styles)(UpdateLoadAllocationModalV2);
